<template>
  <div class="tab-panel">
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="endpoint"
      :auto-load="false"
      :filterConfig="filterConfig"
      :refreshButton="{ show: true, text: t('Atualizar') }"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';

interface DataTableInstance {
  loadData: () => void;
}

interface CellParams {
  data: Record<string, unknown> & { _isFrozenFooter?: boolean };
  value?: unknown;
}

interface StatusInfo {
  label: string;
  color: string;
  bg: string;
}

const emit = defineEmits<{
  'update:total': [total: number];
}>();

const { t } = useI18n();
const dataTableRef = ref<DataTableInstance | null>(null);

const endpoint = '/index.php/mensagens_programadas/carrega_scheduled_messages';

const tableConfig = computed(() => ({
  tableId: 'bottomBarMensagensProgramadas',
  title: '',
  columns: tableColumns.value,
  pagination: true,
  pageLength: 50,
  heightMode: 'fixed',
  height: '300',
}));

const filterConfig = computed(() => ({
  columns: {
    recipient: 'text',
    status: 'select',
  }
}));

const tableColumns = computed(() => [
  {
    data: 'message_id',
    title: t('ID'),
    width: 60,
    resizable: true,
    sortable: true,
    dataType: 'number',
  },
  {
    data: 'recipient',
    title: t('Destinatário'),
    width: 140,
    resizable: true,
    sortable: true,
    dataType: 'text',
  },
  {
    data: 'content',
    title: t('Mensagem'),
    width: 260,
    resizable: true,
    sortable: false,
    dataType: 'text',
    cellRenderer: (params: CellParams): string => {
      if (params.data._isFrozenFooter) return '';
      const text = (params.value as string) || '';
      const truncated = text.length > 80 ? text.substring(0, 80) + '…' : text;
      return `<span title="${text.replace(/"/g, '&quot;')}" style="font-size:12px;">${truncated}</span>`;
    },
  },
  {
    data: 'scheduled_time',
    title: t('Agendada para'),
    width: 150,
    resizable: true,
    sortable: true,
    renderType: 'datetime',
    dataType: 'date',
  },
  {
    data: 'status',
    title: t('Status'),
    width: 110,
    resizable: true,
    sortable: true,
    dataType: 'text',
    cellRenderer: (params: CellParams): string => {
      if (params.data._isFrozenFooter) return '';
      const statusMap: Record<string, StatusInfo> = {
        'pending':    { label: t('Pendente'),     color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
        'processing': { label: t('Processando'),  color: '#3b82f6', bg: 'rgba(59,130,246,0.15)' },
        'sent':       { label: t('Enviada'),      color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
        'failed':     { label: t('Falhou'),       color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
        'cancelled':  { label: t('Cancelada'),    color: '#6b7280', bg: 'rgba(107,114,128,0.15)' },
      };
      const key = String(params.value ?? '');
      const s: StatusInfo = statusMap[key] || { label: key, color: '#6b7280', bg: 'rgba(107,114,128,0.15)' };
      return `<span style="display:inline-block;padding:2px 8px;border-radius:8px;font-size:11px;font-weight:600;color:${s.color};background:${s.bg};">${s.label}</span>`;
    },
  },
]);

const loadData = (): void => {
  log('MessagingBottomBarMensagensProgramadas', 'loadData', 'Loading');
  dataTableRef.value?.loadData();
};

const handleDataLoaded = (data: unknown[]): void => {
  emit('update:total', data.length);
};

const handleDataError = (err: unknown): void => {
  log('MessagingBottomBarMensagensProgramadas', 'handleDataError', 'Error', err, 'error');
};

defineExpose({ loadData });
</script>

<style scoped>
.tab-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
